import React, { useState, useEffect, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { toast } from 'sonner';
import { hardRedirect } from '../utils/navigation';
import { AuthContext } from '../contexts/AuthContext';
import {
  getSocialSummary,
  sendFriendRequest,
  respondToFriendRequest,
  searchSocial,
  APIError
} from '../services/api';
import PageTransition from '../components/ui/PageTransition';
import EmptyState from '../components/ui/EmptyState';

// ─── Animation variants ────────────────────────────────────────────────────

const listContainer = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.06,
    },
  },
};

const listItem = {
  hidden: { opacity: 0, y: 10 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut' } },
};

// ─── Skeleton components ───────────────────────────────────────────────────

const FriendRowSkeleton = () => (
  <div className="flex items-center gap-3 py-3 px-1">
    <div className="w-10 h-10 rounded-full skeleton flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-3.5 w-32 skeleton rounded-md" />
      <div className="h-3 w-20 skeleton rounded-md" />
    </div>
    <div className="w-16 h-7 skeleton rounded-lg" />
  </div>
);

const ConversationSkeleton = () => (
  <div className="flex items-center gap-3 p-3">
    <div className="w-10 h-10 rounded-full skeleton flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-3.5 w-24 skeleton rounded-md" />
      <div className="h-3 w-36 skeleton rounded-md" />
    </div>
    <div className="h-3 w-8 skeleton rounded-md" />
  </div>
);

// ─── Avatar bubble ─────────────────────────────────────────────────────────

const Avatar = ({ name = '', size = 'md', online = false, className = '' }) => {
  const initial = name.charAt(0).toUpperCase() || '?';
  const sizeMap = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
  };
  return (
    <div className={clsx('relative flex-shrink-0', className)}>
      <div
        className={clsx(
          'rounded-full bg-primary-100 text-primary-700 font-semibold flex items-center justify-center',
          sizeMap[size]
        )}
        aria-hidden="true"
      >
        {initial}
      </div>
      {online && (
        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success-500 rounded-full border-2 border-white" />
      )}
    </div>
  );
};

// ─── Messenger Sidebar ─────────────────────────────────────────────────────

const MOCK_CONVERSATIONS = [
  { id: 1, name: 'Alice Johnson',   initials: 'AJ', lastMessage: 'Hey, how\'s the project going?', timestamp: '2m',  unread: 2, online: true  },
  { id: 2, name: 'Project Team',    initials: 'PT', lastMessage: 'Meeting at 3 PM today',           timestamp: '1h',  unread: 0, online: false },
  { id: 3, name: 'Emma Davis',      initials: 'ED', lastMessage: 'Thanks for the help!',            timestamp: '3h',  unread: 1, online: true  },
  { id: 4, name: 'Marcus Webb',     initials: 'MW', lastMessage: 'Sounds good, talk soon',          timestamp: '5h',  unread: 0, online: false },
];

const MessengerSidebar = ({ activeConversation, setActiveConversation }) => {
  const [skeletonDone, setSkeletonDone] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSkeletonDone(true), 800);
    return () => clearTimeout(t);
  }, []);

  return (
    <aside className="hidden lg:flex w-72 flex-col border-r border-surface-200 bg-white h-full">
      {/* Header */}
      <div className="px-4 py-4 border-b border-surface-200">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-body font-semibold text-surface-900">Messages</h2>
          <button
            className="w-7 h-7 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center hover:bg-primary-100 transition-colors"
            aria-label="New message"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
        <button className="w-full bg-primary-500 hover:bg-primary-600 text-white px-3 py-2 rounded-xl text-body-sm font-semibold transition-colors shadow-colored">
          Open Messenger
        </button>
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto">
        {!skeletonDone ? (
          <div className="px-2 py-2 space-y-1">
            {[...Array(4)].map((_, i) => <ConversationSkeleton key={i} />)}
          </div>
        ) : (
          <motion.ul
            variants={listContainer}
            initial="hidden"
            animate="show"
            className="py-2"
            role="list"
          >
            {MOCK_CONVERSATIONS.map((conv) => (
              <motion.li key={conv.id} variants={listItem}>
                <button
                  onClick={() => setActiveConversation(conv)}
                  className={clsx(
                    'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                    activeConversation?.id === conv.id
                      ? 'bg-primary-50'
                      : 'hover:bg-surface-50'
                  )}
                >
                  <Avatar name={conv.name} size="md" online={conv.online} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-body-sm font-medium text-surface-900 truncate">
                        {conv.name}
                      </span>
                      <span className="text-caption text-surface-400 flex-shrink-0">
                        {conv.timestamp}
                      </span>
                    </div>
                    <p className="text-body-sm text-surface-500 truncate mt-0.5">
                      {conv.lastMessage}
                    </p>
                  </div>
                  {conv.unread > 0 && (
                    <span className="w-5 h-5 bg-primary-500 text-white text-caption font-semibold rounded-full flex items-center justify-center flex-shrink-0">
                      {conv.unread}
                    </span>
                  )}
                </button>
              </motion.li>
            ))}
          </motion.ul>
        )}
      </div>
    </aside>
  );
};

// ─── Chat Interface ────────────────────────────────────────────────────────

const MOCK_MESSAGES = [
  { id: 1, text: 'Hi there! How are you doing?',                                      time: '10:30 AM', outgoing: false },
  { id: 2, text: 'Hey! I\'m doing great, thanks for asking.',                         time: '10:32 AM', outgoing: true  },
  { id: 3, text: 'That\'s wonderful to hear. Are you still working on that project?', time: '10:35 AM', outgoing: false },
];

const ChatInterface = ({ conversation, onClose }) => {
  const [message, setMessage] = useState('');

  const handleSend = () => {
    if (message.trim()) {
      setMessage('');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2 }}
      className="flex flex-col h-full bg-white border-l border-surface-200"
    >
      {/* Chat header */}
      <div className="px-4 py-3 border-b border-surface-200 flex items-center gap-3">
        <Avatar name={conversation.name} size="md" online={conversation.online} />
        <div className="flex-1 min-w-0">
          <p className="text-body-sm font-semibold text-surface-900 truncate">{conversation.name}</p>
          <p className="text-caption text-success-600">{conversation.online ? 'Online' : 'Offline'}</p>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-surface-400 hover:text-surface-700 hover:bg-surface-100 transition-colors"
          aria-label="Close chat"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {MOCK_MESSAGES.map((msg) => (
          <div key={msg.id} className={clsx('flex', msg.outgoing ? 'justify-end' : 'justify-start')}>
            <div
              className={clsx(
                'max-w-[75%] px-3.5 py-2.5 rounded-2xl text-body-sm',
                msg.outgoing
                  ? 'bg-primary-500 text-white rounded-tr-sm'
                  : 'bg-surface-100 text-surface-900 rounded-tl-sm'
              )}
            >
              <p>{msg.text}</p>
              <p className={clsx('text-caption mt-1', msg.outgoing ? 'text-primary-100' : 'text-surface-400')}>
                {msg.time}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-surface-200">
        <div className="flex items-center gap-2 bg-surface-50 rounded-xl border border-surface-200 px-3 py-1.5 focus-within:border-primary-400 focus-within:ring-1 focus-within:ring-primary-400/30 transition-all">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Type a message..."
            className="flex-1 bg-transparent text-body-sm text-surface-900 placeholder:text-surface-400 outline-none"
            aria-label="Message input"
          />
          <button
            onClick={handleSend}
            disabled={!message.trim()}
            className="w-8 h-8 bg-primary-500 hover:bg-primary-600 disabled:bg-surface-200 text-white rounded-lg flex items-center justify-center transition-colors"
            aria-label="Send message"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ─── Right sidebar placeholder ─────────────────────────────────────────────

const EmptyChatPlaceholder = () => (
  <div className="flex flex-col items-center justify-center h-full text-center px-6 bg-surface-50">
    <div className="w-14 h-14 rounded-2xl bg-primary-50 text-primary-400 flex items-center justify-center mb-4">
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    </div>
    <p className="text-body-sm font-medium text-surface-700">Select a conversation</p>
    <p className="text-caption text-surface-400 mt-1">Choose a chat from the sidebar to start messaging</p>
  </div>
);

// ─── Friend request card ───────────────────────────────────────────────────

const FriendRequestCard = ({ request, onAccept, onDecline, compact = false }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleAction = async (action) => {
    setIsLoading(true);
    try {
      if (action === 'accept') {
        await onAccept(request.ID || request.id);
        toast.success(`Friend request from ${request.username} accepted`);
      } else {
        await onDecline(request.ID || request.id);
        toast.success('Friend request declined');
      }
    } catch (err) {
      toast.error(err instanceof APIError ? err.message : 'Failed to process friend request');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={clsx(
        'bg-white border border-surface-200 rounded-2xl shadow-soft-sm',
        compact ? 'p-3' : 'p-4'
      )}
    >
      <div className="flex items-start gap-3 mb-3">
        <Avatar name={request.username || 'SC'} size={compact ? 'sm' : 'md'} />
        <div className="flex-1 min-w-0">
          <p className={clsx('font-semibold text-surface-900 truncate', compact ? 'text-body-sm' : 'text-body')}>
            {request.username || 'Someone'}
          </p>
          {request.timestamp && (
            <p className="text-caption text-surface-500 mt-0.5">{request.timestamp}</p>
          )}
        </div>
      </div>
      {request.message && (
        <p className={clsx('text-surface-500 italic mb-3 line-clamp-2', compact ? 'text-caption' : 'text-body-sm')}>
          "{request.message}"
        </p>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => handleAction('accept')}
          disabled={isLoading}
          className="flex-1 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 text-body-sm py-1.5"
        >
          Accept
        </button>
        <button
          onClick={() => handleAction('decline')}
          disabled={isLoading}
          className="flex-1 bg-surface-100 hover:bg-surface-200 text-surface-700 rounded-xl font-semibold transition-colors disabled:opacity-50 text-body-sm py-1.5"
        >
          Decline
        </button>
      </div>
    </div>
  );
};

// ─── Friend row (Threads-style) ────────────────────────────────────────────

const FriendRow = ({ person, actionLabel, onAction }) => (
  <motion.div
    variants={listItem}
    className="flex items-center gap-3 py-3 border-b border-surface-100 last:border-0"
  >
    <Avatar name={person.username || person.business_name || '?'} size="md" online={person.online} />
    <div className="flex-1 min-w-0">
      <p className="text-body-sm font-semibold text-surface-900 truncate">
        {person.username || person.Username || person.business_name || person.BusinessName}
      </p>
      <p className="text-caption text-surface-400 mt-0.5 truncate">
        {person.first_name || person.FirstName
          ? `${person.first_name || person.FirstName} ${person.last_name || person.LastName || ''}`
          : person.business_type || person.BusinessType || ''}
      </p>
    </div>
    {onAction && (
      <button
        onClick={() => onAction(person.ID || person.id)}
        className="flex-shrink-0 px-3 py-1.5 bg-primary-50 hover:bg-primary-100 text-primary-700 text-caption font-semibold rounded-lg transition-colors"
      >
        {actionLabel}
      </button>
    )}
  </motion.div>
);

// ─── Group card ────────────────────────────────────────────────────────────

const GroupCard = ({ group }) => (
  <motion.div
    variants={listItem}
    className="bg-white border border-surface-200 rounded-2xl shadow-soft-sm p-4 min-w-[260px] flex-shrink-0 flex flex-col gap-3"
  >
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-primary-100 text-primary-700 font-bold flex items-center justify-center text-body-sm flex-shrink-0">
        {(group.name || 'G').charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-body-sm font-semibold text-surface-900 truncate">
          {group.name || 'Weekend Warriors'}
        </p>
        <p className="text-caption text-surface-400">
          {group.memberCount || 18} members
        </p>
      </div>
    </div>
    <p className="text-body-sm text-surface-500 line-clamp-2">
      {group.description || 'Adventure and outdoor activities for the whole group.'}
    </p>
    <button className="w-full bg-primary-500 hover:bg-primary-600 text-white text-body-sm font-semibold px-4 py-2 rounded-xl transition-colors">
      Manage Group
    </button>
  </motion.div>
);

// ─── Tab bar ───────────────────────────────────────────────────────────────

const TabBar = ({ tabs, activeTab, onTabChange }) => (
  <div className="border-b border-surface-200 mb-6 overflow-x-auto">
    <nav className="flex gap-0 min-w-max" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={activeTab === tab.id}
          onClick={() => onTabChange(tab.id)}
          className={clsx(
            'flex items-center gap-2 px-4 py-3 text-body-sm font-medium border-b-2 transition-colors whitespace-nowrap',
            activeTab === tab.id
              ? 'text-primary-600 border-primary-500'
              : 'text-surface-500 border-transparent hover:text-surface-800 hover:border-surface-300'
          )}
        >
          {tab.label}
          {tab.count > 0 && (
            <span
              className={clsx(
                'px-1.5 py-0.5 rounded-full text-caption font-semibold',
                activeTab === tab.id
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-surface-100 text-surface-500'
              )}
            >
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </nav>
  </div>
);

// ─── Error display ─────────────────────────────────────────────────────────

const ErrorDisplay = ({ error, onRetry }) => (
  <div className="bg-error-50 border border-error-400/20 rounded-2xl p-4 mb-4 flex items-start gap-3">
    <div className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5">
      <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
    </div>
    <div className="flex-1">
      <p className="text-body-sm font-medium text-error-600">{error}</p>
      {onRetry && (
        <button onClick={onRetry} className="mt-1.5 text-body-sm text-error-500 underline hover:text-error-400">
          Try again
        </button>
      )}
    </div>
  </div>
);

// ─── Skeleton page ─────────────────────────────────────────────────────────

const SocialPageSkeleton = () => (
  <div className="min-h-screen bg-surface-50">
    <div className="bg-white border-b border-surface-200">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
        <div className="w-11 h-11 rounded-full skeleton flex-shrink-0" />
        <div className="flex-1 max-w-md space-y-2">
          <div className="h-4 w-40 skeleton rounded-md" />
          <div className="h-3 w-24 skeleton rounded-md" />
        </div>
        <div className="ml-auto h-10 w-72 skeleton rounded-xl" />
      </div>
    </div>
    <div className="max-w-7xl mx-auto flex h-[calc(100vh-73px)]">
      <div className="hidden lg:block w-72 border-r border-surface-200 bg-white px-4 py-4 space-y-4">
        <div className="h-9 skeleton rounded-xl" />
        {[...Array(4)].map((_, i) => <ConversationSkeleton key={i} />)}
      </div>
      <div className="flex-1 p-6 space-y-4">
        <div className="flex gap-6 border-b border-surface-200 pb-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-4 w-16 skeleton rounded-md" />
          ))}
        </div>
        <div className="h-4 w-32 skeleton rounded-md" />
        {[...Array(6)].map((_, i) => <FriendRowSkeleton key={i} />)}
      </div>
    </div>
  </div>
);

// ─── Main page component ───────────────────────────────────────────────────

const MOCK_GROUPS = [
  { id: 1, name: 'Weekend Warriors',     memberCount: 18, description: 'Adventure and outdoor activities every weekend.' },
  { id: 2, name: 'Cooking Enthusiasts',  memberCount: 12, description: 'Share recipes, cook-alongs, and food tips.' },
  { id: 3, name: 'Book Club Monthly',    memberCount: 8,  description: 'Monthly themed book discussions and reviews.' },
  { id: 4, name: 'Fitness Buddies',      memberCount: 25, description: 'Workout partners, challenges, and progress.' },
  { id: 5, name: 'Gaming Squad',         memberCount: 15, description: 'Casual and competitive gaming sessions.' },
  { id: 6, name: 'Travel Buddies',       memberCount: 22, description: 'Group trip planning and travel stories.' },
];


function SocialPage() {
  const { currentUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('friends');
  const [activeConversation, setActiveConversation] = useState(null);
  const [socialData, setSocialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Show only real pending requests. This used to fall back to a hardcoded
  // list whenever the real one was empty -- i.e. for EVERY new user -- so the
  // Requests tab showed three fabricated people, the badge counted them, and
  // Accept posted a made-up friendship_id to the live API and errored. The
  // empty state below already handles "no requests" properly.
  const displayRequests = socialData?.pending_friend_requests || [];

  const loadSocialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getSocialSummary();
      setSocialData(response.data);
    } catch (err) {
      console.error('Failed to load social data:', err);
      let msg = 'Failed to load social data';
      if (err instanceof APIError) {
        if (err.code === 'UNAUTHORIZED' || err.status === 401) {
          msg = 'Please log in to access social features.';
        } else if (err.code === 'NETWORK_ERROR') {
          msg = 'Network error. Please check your connection and try again.';
        } else {
          msg = err.message;
        }
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setError('Please log in to access social features.');
      setLoading(false);
      return;
    }
    const accountType = currentUser?.account_type || currentUser?.AccountType;
    if (accountType && accountType !== 'user') {
      setError('Social features are only available for user accounts.');
      setLoading(false);
      return;
    }
    loadSocialData();
  }, [currentUser, loadSocialData]);

  const handleFriendRequestResponse = async (friendshipId, action) => {
    try {
      await respondToFriendRequest(friendshipId, action);
      await loadSocialData();
    } catch (err) {
      setError(err instanceof APIError ? err.message : 'Failed to respond to friend request');
      throw err;
    }
  };

  const handleSendFriendRequest = async (userId) => {
    try {
      await sendFriendRequest(userId);
      toast.success('Friend request sent');
    } catch (err) {
      toast.error(err instanceof APIError ? err.message : 'Failed to send friend request');
      throw err;
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      setSearchLoading(true);
      setSearchError(null);
      const response = await searchSocial(searchQuery.trim());
      setSearchResults(response.data || []);
    } catch (err) {
      setSearchError(err instanceof APIError ? err.message : 'Search failed');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const tabs = [
    { id: 'friends',     label: 'Friends',       count: socialData?.friends?.length || 0           },
    { id: 'groups',      label: 'Group CliQQs',  count: 0                                           },
    { id: 'following',   label: 'Following',     count: socialData?.following?.length || 0          },
    { id: 'communities', label: 'Communities',   count: socialData?.organizations?.length || 0      },
    { id: 'requests',    label: 'Requests',      count: displayRequests?.length || 0                },
  ];

  if (loading) return <SocialPageSkeleton />;

  if (error) {
    return (
      <div className="min-h-screen bg-surface-50">
        <div className="max-w-2xl mx-auto p-6">
          <ErrorDisplay
            error={error}
            onRetry={
              currentUser?.account_type === 'user' || currentUser?.AccountType === 'user'
                ? loadSocialData
                : null
            }
          />
          {error.includes('log in') && (
            <div className="mt-4 text-center">
              <button
                onClick={() => hardRedirect('/login')}
                className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors shadow-colored"
              >
                Go to Login
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <PageTransition className="min-h-screen bg-surface-50">
      {/* ── Page header ── */}
      <header className="bg-white border-b border-surface-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-primary-500 text-white font-bold flex items-center justify-center text-body-sm flex-shrink-0">
              C
            </div>
            <div className="hidden sm:block min-w-0">
              <h1 className="text-body font-bold text-primary-600 leading-tight">CliQQ Social</h1>
              <p className="text-caption text-surface-400">
                {currentUser?.username ? `@${currentUser.username}` : 'Welcome back'}
              </p>
            </div>
          </div>

          {/* Search bar */}
          <div className="flex-1 max-w-lg ml-2">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" aria-hidden="true">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search friends, groups, communities..."
                aria-label="Search social network"
                className="w-full pl-9 pr-4 py-2 bg-surface-100 border border-surface-200 rounded-xl text-body-sm text-surface-900 placeholder:text-surface-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-all"
              />
              {searchLoading && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </span>
              )}
              {!searchLoading && searchQuery && (
                <button
                  onClick={handleSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-500 hover:text-primary-700 text-caption font-semibold"
                >
                  Search
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── Main layout ── */}
      <div className="max-w-7xl mx-auto flex" style={{ height: 'calc(100vh - 57px)' }}>

        {/* Left: Messenger sidebar */}
        <MessengerSidebar
          activeConversation={activeConversation}
          setActiveConversation={setActiveConversation}
        />

        {/* Center: tab content */}
        <main className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">

          {/* Search results overlay */}
          <AnimatePresence>
            {searchQuery && (
              <motion.div
                key="search-results"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="mb-6 bg-white rounded-2xl border border-surface-200 shadow-soft-md overflow-hidden"
              >
                <div className="flex items-center justify-between px-5 py-3 border-b border-surface-100">
                  <p className="text-body-sm font-semibold text-surface-900">
                    Results for "<span className="text-primary-600">{searchQuery}</span>"
                  </p>
                  <button
                    onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                    className="text-caption text-surface-400 hover:text-surface-700"
                  >
                    Clear
                  </button>
                </div>
                {searchError ? (
                  <div className="p-4"><ErrorDisplay error={searchError} /></div>
                ) : searchResults.length === 0 ? (
                  <div className="py-8 text-center text-body-sm text-surface-400">No results found.</div>
                ) : (
                  <motion.div
                    variants={listContainer}
                    initial="hidden"
                    animate="show"
                    className="divide-y divide-surface-100"
                  >
                    {searchResults.map((result) => (
                      <FriendRow
                        key={result.ID || result.id}
                        person={result}
                        actionLabel={result.account_type === 'user' ? 'Add friend' : 'Follow'}
                        onAction={result.account_type === 'user' ? handleSendFriendRequest : null}
                      />
                    ))}
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Tab bar */}
          <TabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

          {/* Tab panels */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            >

              {/* FRIENDS */}
              {activeTab === 'friends' && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-h3 text-surface-900">
                      Friends
                      {socialData?.friends?.length > 0 && (
                        <span className="ml-2 text-body text-surface-400 font-normal">
                          {socialData.friends.length}
                        </span>
                      )}
                    </h2>
                  </div>
                  {socialData?.friends?.length === 0 ? (
                    <EmptyState
                      icon={
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      }
                      title="No friends yet"
                      description="Use the search bar to find and add friends."
                    />
                  ) : (
                    <motion.div variants={listContainer} initial="hidden" animate="show">
                      {socialData.friends.map((friend) => (
                        <FriendRow key={friend.ID || friend.id} person={friend} />
                      ))}
                    </motion.div>
                  )}
                </>
              )}

              {/* GROUP CLIQQS */}
              {activeTab === 'groups' && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-h3 text-surface-900">Group CliQQs</h2>
                    <button className="flex items-center gap-1.5 text-primary-600 hover:text-primary-700 text-body-sm font-semibold transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      New group
                    </button>
                  </div>
                  <div className="text-center py-10 text-body-sm text-surface-400">
                    <p>No group CliQQs yet.</p>
                    <p className="mt-1">Create or join groups to see them here.</p>
                  </div>
                </>
              )}

              {/* FOLLOWING */}
              {activeTab === 'following' && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-h3 text-surface-900">
                      Following
                      {socialData?.following?.length > 0 && (
                        <span className="ml-2 text-body text-surface-400 font-normal">
                          {socialData.following.length}
                        </span>
                      )}
                    </h2>
                  </div>
                  {socialData?.following?.length === 0 ? (
                    <EmptyState
                      icon={
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      }
                      title="Not following anyone yet"
                      description="Search for businesses to follow their updates."
                    />
                  ) : (
                    <motion.div variants={listContainer} initial="hidden" animate="show">
                      {socialData.following.map((biz) => (
                        <FriendRow key={biz.ID || biz.id} person={biz} />
                      ))}
                    </motion.div>
                  )}
                </>
              )}

              {/* COMMUNITIES */}
              {activeTab === 'communities' && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-h3 text-surface-900">
                      Communities
                      {socialData?.organizations?.length > 0 && (
                        <span className="ml-2 text-body text-surface-400 font-normal">
                          {socialData.organizations.length}
                        </span>
                      )}
                    </h2>
                  </div>
                  {socialData?.organizations?.length === 0 ? (
                    <EmptyState
                      icon={
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      }
                      title="No communities yet"
                      description="Join communities to connect with like-minded people."
                    />
                  ) : (
                    <motion.div variants={listContainer} initial="hidden" animate="show">
                      {socialData.organizations.map((org) => (
                        <FriendRow key={org.ID || org.id} person={{ ...org, username: org.name || org.Name }} />
                      ))}
                    </motion.div>
                  )}
                </>
              )}

              {/* REQUESTS */}
              {activeTab === 'requests' && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-h3 text-surface-900">
                      Friend Requests
                      {displayRequests?.length > 0 && (
                        <span className="ml-2 text-body text-surface-400 font-normal">
                          {displayRequests.length}
                        </span>
                      )}
                    </h2>
                  </div>
                  {displayRequests?.length === 0 ? (
                    <EmptyState
                      icon={
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                      }
                      title="No pending requests"
                      description="Friend requests will appear here when you receive them."
                    />
                  ) : (
                    <motion.div
                      variants={listContainer}
                      initial="hidden"
                      animate="show"
                      className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
                    >
                      {displayRequests.map((req) => (
                        <motion.div key={req.ID || req.id} variants={listItem}>
                          <FriendRequestCard
                            request={req}
                            onAccept={(id) => handleFriendRequestResponse(id, 'accept')}
                            onDecline={(id) => handleFriendRequestResponse(id, 'reject')}
                          />
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </>
              )}

            </motion.div>
          </AnimatePresence>

          {/* ── Invite Groups section ── */}
          <section className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-h3 text-surface-900">Invite Groups</h2>
              <button className="flex items-center gap-1.5 text-primary-600 hover:text-primary-700 text-body-sm font-semibold transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New group
              </button>
            </div>
            <div className="flex overflow-x-auto gap-4 pb-4 -mx-1 px-1">
              <motion.div
                variants={listContainer}
                initial="hidden"
                animate="show"
                className="flex gap-4"
              >
                {MOCK_GROUPS.map((group) => (
                  <GroupCard key={group.id} group={group} />
                ))}
              </motion.div>
            </div>
          </section>
        </main>

        {/* Right: chat panel */}
        <aside className="hidden xl:flex w-80 flex-col border-l border-surface-200">
          {activeConversation ? (
            <ChatInterface
              conversation={activeConversation}
              onClose={() => setActiveConversation(null)}
            />
          ) : displayRequests?.length > 0 ? (
            <div className="flex flex-col h-full bg-white overflow-y-auto">
              <div className="px-5 py-4 border-b border-surface-200 flex items-center justify-between">
                <h2 className="text-body font-semibold text-surface-900">Pending Requests</h2>
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-caption font-semibold rounded-full">
                  {displayRequests.length}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                {displayRequests.map((req) => (
                  <FriendRequestCard
                    key={req.ID || req.id}
                    request={req}
                    onAccept={(id) => handleFriendRequestResponse(id, 'accept')}
                    onDecline={(id) => handleFriendRequestResponse(id, 'reject')}
                    compact
                  />
                ))}
              </div>
            </div>
          ) : (
            <EmptyChatPlaceholder />
          )}
        </aside>
      </div>
    </PageTransition>
  );
}

export default SocialPage;
